import { z } from 'zod'
import { ref } from '@omnea-app/packages/universal/references/refKinds'
import { RequestStepTaskTypeEnum } from '@omnea-app/packages/universal/tasks/taskEnums'

export const ReviewTaskSchema = z
  .object({
    formTemplateId: ref('formTemplate').optional(),
    reviewTaskTemplateId: ref('reviewTaskTemplate').optional(),
    // Written only into a request's frozen model at request start, never by the builder, so a request keeps the version it began with.
    reviewTaskTemplateVersion: z.number().int().positive().optional(),
    type: z.literal(RequestStepTaskTypeEnum.REVIEW),
  })
  .refine((task) => (task.formTemplateId === undefined) !== (task.reviewTaskTemplateId === undefined), {
    message: 'A review task needs exactly one of formTemplateId and reviewTaskTemplateId',
  })
export type ReviewTask = z.infer<typeof ReviewTaskSchema>

// A review that reads a form template, as every review did before review templates.
export type LegacyReviewTask = ReviewTask & { formTemplateId: string }

// A review that reads a review task template instead of one form.
export type ReviewTaskV2 = ReviewTask & { reviewTaskTemplateId: string }

// The config a review task v2 row stores. The title is copied from the pinned version when the step starts, as My Tasks shows it.
export type ReviewTaskV2RowConfig = {
  type: RequestStepTaskTypeEnum.REVIEW
  reviewTaskTemplateId: string
  reviewTaskTemplateVersion: number
  title: string
}

export const FillInFormTaskSchema = z.object({
  formTemplateId: ref('formTemplate'),
  type: z.literal(RequestStepTaskTypeEnum.FILL_IN),
})
export type FillInFormTask = z.infer<typeof FillInFormTaskSchema>

export const EsignFillInTaskSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  // Belongs to the e-sign provider, not to a form template — nothing in this database owns it.
  remoteTemplateId: z.string(),
  type: z.literal(RequestStepTaskTypeEnum.ESIGN_FILL_IN),
  remoteFolderId: z.string().optional(),
  ownerEmail: z.string(),
})
export type EsignFillInTask = z.infer<typeof EsignFillInTaskSchema>

export const ExternalOperationTaskSchema = z.object({
  type: z.literal(RequestStepTaskTypeEnum.EXTERNAL_OPERATION),
  formTemplateId: ref('formTemplate').optional(),
})
export type ExternalOperationTask = z.infer<typeof ExternalOperationTaskSchema>

export const ContentGenerationTaskSchema = z.object({
  formTemplateId: ref('formTemplate'),
  type: z.literal(RequestStepTaskTypeEnum.CONTENT_GENERATION),
})
export type ContentGenerationTask = z.infer<typeof ContentGenerationTaskSchema>

export const RfxContentGenerationTaskSchema = z.object({
  formTemplateId: ref('formTemplate'),
  type: z.literal(RequestStepTaskTypeEnum.RFX_CONTENT_GENERATION),
})
export type RfxContentGenerationTask = z.infer<typeof RfxContentGenerationTaskSchema>

export const SummaryApprovalTaskSchema = z.object({
  type: z.literal(RequestStepTaskTypeEnum.SUMMARY_APPROVAL),
  title: z.string(),
})
export type SummaryApprovalTask = z.infer<typeof SummaryApprovalTaskSchema>

export const AccountingSupplierDataSyncTaskSchema = z.object({
  type: z.literal(RequestStepTaskTypeEnum.ACCOUNTING_SUPPLIER_DATA_SYNC),
})
export type AccountingSupplierDataSyncTask = z.infer<typeof AccountingSupplierDataSyncTaskSchema>

export type FillInOrReviewTask = LegacyReviewTask | FillInFormTask

export type RequestFormBackedTask = FillInOrReviewTask | ContentGenerationTask | RfxContentGenerationTask

export const EsignTaskSchema = EsignFillInTaskSchema // Add EsignReviewTask here in the future
export type EsignTask = z.infer<typeof EsignTaskSchema>

export const BlockTaskSchema = z.discriminatedUnion('type', [
  ReviewTaskSchema,
  FillInFormTaskSchema,
  EsignTaskSchema,
  SummaryApprovalTaskSchema,
  AccountingSupplierDataSyncTaskSchema,
  ExternalOperationTaskSchema,
  ContentGenerationTaskSchema,
  RfxContentGenerationTaskSchema,
])
export type BlockTask = z.infer<typeof BlockTaskSchema>
